import React, {useCallback, useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import PharmacyDetailDialogNew from 'src/pages-new/patient/tabs/pharmacies/PharmacyDetailDialogNew'

interface PharmaciesTabProps {
  patientId: string
}

interface PharmacyRow {
  _id: string
  [key: string]: any
}

interface PharmacyColumnConfig {
  id: string
  label: string
  type?: string
}

const PHARMACY_TYPE_OPTIONS = [
  {value: 'EXTERNAL', label: 'Esterna'},
  {value: 'HOSPITAL', label: 'Ospedaliera'},
]

// No "status attivazione" column: the API has no real activation-status field
// for a pharmacy, so it must never be shown (previously it was faked with a
// hardcoded 'Attiva' default — removed).
const DEFAULT_COLUMNS: PharmacyColumnConfig[] = [
  {id: 'isPrimary', label: 'Riferimento', type: 'boolean'},
  {id: 'code', label: 'Codice'},
  {id: 'name', label: 'Nome'},
  {id: 'typeReadable', label: 'Tipo'},
  {id: 'phoneNumber', label: 'Telefono'},
]

// On mobile, "Nome" becomes the card's title (mobileCardHeader) — the other
// fields (Riferimento, Codice, Tipo, Telefono) sit in the plain field grid below.
function buildColumns(columnConfig: PharmacyColumnConfig[]): ColumnDef<PharmacyRow>[] {
  return columnConfig
    .map(col => {
      // Columns coming from the PSP configurator may omit `type` (e.g. for
      // "Riferimento") — fall back to the built-in defaults by id so
      // behavior stays the same regardless of config source.
      const defaults = DEFAULT_COLUMNS.find(defaultCol => defaultCol.id === col.id)
      const type = col.type ?? defaults?.type

      return {
        key: col.id,
        header: col.label,
        mobileCardHeader: col.id === 'name',
        render: (row: PharmacyRow) => {
          if (type === 'boolean') {
            return row[col.id]
              ? <CheckCircleOutlineIcon sx={{color: '#16A34A', fontSize: 20}} />
              : <Typography variant="body2">{'—'}</Typography>
          }
          const value = row[col.id] ?? '—'
          return col.id === 'name'
            ? <Typography variant="body2" fontWeight={600}>{value}</Typography>
            : <Typography variant="body2">{value}</Typography>
        },
      }
    })
}

// The "Riferimento" pharmacy (isPrimary) must always be shown first; the
// rest are ordered by code.
function sortPharmacies(pharmacies: PharmacyRow[]): PharmacyRow[] {
  return [...pharmacies].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1
    if (!a.isPrimary && b.isPrimary) return 1
    return String(a.code ?? '').localeCompare(String(b.code ?? ''))
  })
}

export default function PharmaciesTab({patientId}: PharmaciesTabProps): JSX.Element {
  const pharmaciesColumnOptions: PharmacyColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.pharmaciesConfiguration?.columnOptions
  )

  const columnConfig = pharmaciesColumnOptions?.length > 0 ? pharmaciesColumnOptions : DEFAULT_COLUMNS

  const [rows, setRows] = useState<PharmacyRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})
  const [pharmacyDetail, setPharmacyDetail] = useState<PharmacyRow>()
  const [openDetailDialog, setOpenDetailDialog] = useState(false)

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getPatientsPharmacies(patientId, setStatus, pagination),
      apiClient.getPatientsPharmaciesCount(patientId, setStatusCount),
    ])
      .then(([pharmacies, countResult]) => {
        if (Array.isArray(pharmacies)) {
          const mapped = pharmacies.map((pharmacy: any) => ({
            ...pharmacy,
            typeReadable: PHARMACY_TYPE_OPTIONS.find(option => option.value === pharmacy.type)?.label,
          }))
          setRows(sortPharmacies(mapped))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleShowDetail = (row: PharmacyRow) => {
    setPharmacyDetail(row)
    setOpenDetailDialog(true)
  }

  const columns: ColumnDef<PharmacyRow>[] = [
    ...buildColumns(columnConfig),
    {
      key: 'actions',
      header: '',
      width: 56,
      isActionColumn: true,
      render: (row: PharmacyRow) => (
        <ActionIconButton variant="info" tooltip="Dettaglio" onClick={() => handleShowDetail(row)} />
      ),
    },
  ]

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare le farmacie.'}
        </InlineMessage>
      )}

      <DataTable<PharmacyRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessuna farmacia trovata"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      <PharmacyDetailDialogNew
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        detail={pharmacyDetail}
      />
    </Box>
  )
}
